/* eslint-disable fp/no-mutation */
import React from 'react'
import {formatPhoneNumber} from 'react-phone-number-input'
import {useDispatch} from 'react-redux'
import styled from '@emotion/styled'
import useParams from 'components/data/URLParams/useParams'
import {trackEventV1} from 'modules/analytics/actions'
import {
  EventAction,
  EventCategory,
  EventName
} from 'modules/analytics/actions/trackEventV1'
import {getPageSpace} from 'modules/analytics/trackEventV1Helpers'

import {ButtonLink} from '@daedalus/atlas/ButtonLink'
import {useDeviceLayout} from '@daedalus/atlas/context/deviceLayout'
import {Grid} from '@daedalus/atlas/helpers/Grid'
import {Icon} from '@daedalus/atlas/Icon'
import {Text} from '@daedalus/atlas/Text'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@daedalus/core/src/auth/modules/actions'
import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'

type PlacementType = 'rsp' | 'pp' | 'friction' | 'bookingError'

type CallToBookPropsType = {
  placementType: PlacementType
}

type LabelElementPropsType = {
  placementType: PlacementType
  phoneNumber: string
}

const ICON_SIZE = 16

const IconWrapper = styled.div`
  height: ${ICON_SIZE}px;
  margin-top: 3px;
`

type USPhones = {
  rsp: string
  pp: string
  friction: string
  bookingError: string
}

type US = 'US'
type OtherCountries = 'CA' | 'GB' | 'NZ' | 'AU'
type Countries = US | OtherCountries
type Phone<T> = T extends US ? {desktop: USPhones; mobile: USPhones} : string
type PhoneNumbers = Record<Countries, Phone<Countries>>

const phoneNumbers: PhoneNumbers = {
  US: {
    desktop: {
      rsp: '+18662637084',
      pp: '+18662663381',
      friction: '+18662663413',
      bookingError: '+18662675898'
    },
    mobile: {
      rsp: '+18664532278',
      pp: '+18664532442',
      friction: '+18665276985',
      bookingError: '+18665276990'
    }
  },
  CA: '+18884160120',
  GB: '+448000581627',
  NZ: '+64800440831',
  AU: '+611800934024'
}

const PromoLabel = (
  <FormattedMessageWrapper
    id="callToBook.promo"
    defaultMessage="Call us for great deals"
  />
)

const HelpLabel = (
  <FormattedMessageWrapper
    id="callToBook.help"
    defaultMessage="Need help booking?"
  />
)

const Label = ({placementType}: CallToBookPropsType) =>
  ({
    rsp: PromoLabel,
    pp: HelpLabel,
    friction: HelpLabel,
    bookingError: HelpLabel
  }[placementType])

const LabelElement = ({placementType, phoneNumber}: LabelElementPropsType) => (
  <div data-id="CallToBookCta">
    <Grid container spacing="s300">
      <Grid mobileSm="fit">
        <IconWrapper>
          <Icon name="Phone" size="sm" />
        </IconWrapper>
      </Grid>
      <Grid mobileSm="fill">
        <Grid container spacing="s300">
          <Grid mobileSm="fit">
            <Label placementType={placementType} />
          </Grid>
          <Grid mobileSm="fit">{formatPhoneNumber(phoneNumber)}</Grid>
        </Grid>
      </Grid>
    </Grid>
  </div>
)

const CallToBook = ({placementType}: CallToBookPropsType) => {
  const {isDesktop, isMobile} = useDeviceLayout()
  const dispatch = useDispatch()
  const urlParams = useParams()
  const {userCountry} = urlParams

  const phoneNumber =
    userCountry === 'US'
      ? (phoneNumbers.US as Phone<US>)[isDesktop ? 'desktop' : 'mobile'][
          placementType
        ]
      : (phoneNumbers[userCountry as OtherCountries] as string)

  const handleClick = () => {
    const space = getPageSpace(window.location.pathname)

    dispatch(
      trackEventV1({
        name: EventName.CALL_TO_BOOK_CLICK,
        category: EventCategory.USER_ACTION,
        space,
        label: placementType,
        value: phoneNumber,
        action: EventAction.CLICK,
        urlParams
      })
    )

    setTimeout(() => {
      window.location.href = `tel:${phoneNumber}`
    }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
  }

  if (!phoneNumber) return null

  return (
    <Text variant="labelM" as="div">
      {isMobile && (
        <ButtonLink onClick={handleClick}>
          <LabelElement
            placementType={placementType}
            phoneNumber={phoneNumber}
          />
        </ButtonLink>
      )}

      {!isMobile && (
        <LabelElement placementType={placementType} phoneNumber={phoneNumber} />
      )}
    </Text>
  )
}

export default CallToBook
